import React, { useState, useEffect } from 'react';
import { createPreference, createWompiPaymentLink } from '../../services/financeService';
import { Modal, Box, Typography, TextField, Button, CircularProgress, Alert, InputAdornment } from '@mui/material';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 400 },
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  maxHeight: '90vh',
  overflowY: 'auto',
};

// Debe coincidir con WOMPI_MIN_RECHARGE_AMOUNT del backend.
const MIN_RECHARGE_AMOUNT = 50000;

// En Colombia el punto separa miles ("200.000"); parseFloat lo leía como 200.
// Solo se aceptan pesos enteros, así que se descarta todo lo que no sea dígito.
const parseAmount = (value) => {
  const digits = String(value).replace(/\D/g, '');
  return digits ? parseInt(digits, 10) : 0;
};

const formatCOP = (value) => new Intl.NumberFormat('es-CO').format(value);

const AddCreditModal = ({ isOpen, userId, onClose, onCreditAdded }) => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setAmount('');
      setDescription('');
      setError(null);
      setLoading(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const numericAmount = parseAmount(amount);
    if (numericAmount < MIN_RECHARGE_AMOUNT) {
      setError(`El monto mínimo de recarga es $${formatCOP(MIN_RECHARGE_AMOUNT)}.`);
      return;
    }
    if (!description) {
      setError('Por favor, introduce una descripción.');
      return;
    }

    setLoading(true);
    setError(null);

    const paymentGateway = import.meta.env.VITE_PAYMENT_GATEWAY;

    if (paymentGateway === 'WOMPI') {
      try {
        const data = await createWompiPaymentLink(numericAmount, description, userId);
        if (data.data?.id) {
          window.location.href = `https://checkout.wompi.co/l/${data.data.id}`;
        } else {
          setError('No se pudo generar el enlace de pago de Wompi.');
        }
      } catch (err) {
        const detail = err.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Error al crear el enlace de pago de Wompi. Inténtalo de nuevo.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    } else { // MercadoPago (Checkout Pro)
      try {
        const data = await createPreference(numericAmount, description, userId);
        const initPoint = data?.init_point || data?.sandbox_init_point;
        if (!initPoint) {
          setError('No se pudo obtener el link de checkout de Mercado Pago.');
          return;
        }
        window.location.href = initPoint;
      } catch (err) {
        setError('Error al crear la preferencia de pago. Inténtalo de nuevo.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="add-credit-modal-title"
    >
      <Box sx={modalStyle}>
        <Typography id="add-credit-modal-title" variant="h6" component="h2">
          Añadir Saldo
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="amount"
            label="Monto"
            name="amount"
            type="text"
            inputMode="numeric"
            placeholder="Ej: 200000"
            value={amount ? formatCOP(parseAmount(amount)) : ''}
            onChange={(e) => setAmount(String(parseAmount(e.target.value) || ''))}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            helperText={`Monto mínimo: $${formatCOP(MIN_RECHARGE_AMOUNT)}`}
            autoFocus
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="description"
            label="Descripción"
            name="description"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button onClick={onClose} disabled={loading} sx={{ mr: 1 }}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Continuar'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddCreditModal;
